import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import {
  Clock, Plus, MapPin, Sparkles, AlertTriangle, Kanban, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import EchoPatientFlow from './EchoPatientFlow';
import { useAppMode } from '../../contexts/AppModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { capabilitiesFor } from '../../lib/permissions';
import * as schedulerApi from '../../lib/api/schedulerApi';

const HOURS = ['8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'];

/* ───────────────────────────────────────────────────────────────────
 * Demo-only fixture. Rendered ONLY in demo mode; real-mode populates
 * from `schedulerApi.listAppointmentsPreferEnriched`. Guarded by the
 * `real_mode_no_mock_dashboard.test.js` regression scanner.
 * ─────────────────────────────────────────────────────────────────── */
const DEMO_APPOINTMENTS = [
  { id: 1, patient: 'John Smith', patientId: 'P001', time: '9:00 AM', duration: 30, type: 'Follow-up', provider: 'Dr. Chen', status: 'checked_in', room: 'Room 1', noShowRisk: 5 },
  { id: 2, patient: 'Maria Garcia', patientId: 'P002', time: '9:30 AM', duration: 45, type: 'New Patient', provider: 'Dr. Wilson', status: 'in_exam', room: 'Room 2', noShowRisk: 12 },
  { id: 3, patient: 'Robert Johnson', patientId: 'P003', time: '10:00 AM', duration: 20, type: 'Sick Visit', provider: 'Dr. Chen', status: 'waiting', room: null, noShowRisk: 8 },
  { id: 4, patient: 'Emily Davis', patientId: 'P004', time: '10:30 AM', duration: 30, type: 'Follow-up', provider: 'Dr. Chen', status: 'scheduled', room: null, noShowRisk: 22 },
  { id: 5, patient: 'James Taylor', patientId: 'P005', time: '11:00 AM', duration: 45, type: 'Wellness', provider: 'Dr. Wilson', status: 'scheduled', room: null, noShowRisk: 35 },
  { id: 6, patient: 'Sarah Williams', patientId: 'P006', time: '1:00 PM', duration: 30, type: 'Follow-up', provider: 'Dr. Chen', status: 'scheduled', room: null, noShowRisk: 15 },
  { id: 7, patient: 'David Wilson', patientId: 'P001', time: '2:00 PM', duration: 45, type: 'New Patient', provider: 'Dr. Wilson', status: 'scheduled', room: null, noShowRisk: 42 },
  { id: 8, patient: 'Lisa Brown', patientId: 'P002', time: '3:00 PM', duration: 20, type: 'Sick Visit', provider: 'Dr. Chen', status: 'scheduled', room: null, noShowRisk: 10 },
];

const STATUS_CONFIG = {
  checked_in: { label: 'Checked In', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  in_exam: { label: 'In Exam', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  waiting: { label: 'Waiting', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  scheduled: { label: 'Scheduled', color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-500', dot: 'bg-slate-400' },
  no_show: { label: 'No Show', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
};

/**
 * Normalize the backend appointment doc to the flat row shape this
 * page already renders. Defensive across `appointments[]` (basic) and
 * `items[]` (enriched) envelopes.
 */
const normalizeAppt = (a, idx = 0) => {
  const start = a.start ? new Date(a.start) : null;
  const end   = a.end   ? new Date(a.end)   : null;
  const duration = start && end ? Math.max(1, Math.round((end - start) / 60000)) : (a.duration_min || 30);
  const status = (a.status === 'booked' || !a.status) ? 'scheduled' : a.status;
  return {
    id:          a.id || `apt-${idx}`,
    patient:     a.patient_name || a.patient || '—',
    patientId:   a.patient_id || a.patientId || '',
    time:        a.start_label || (start ? start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''),
    duration,
    type:        a.visit_type_name || a.visit_type || a.type || '',
    provider:    a.provider_name || a.entity_name || a.provider || '',
    status,
    room:        a.room || a.room_name || null,
    noShowRisk:  a.no_show_risk ?? a.noShowRisk ?? 0,
  };
};

const EchoSchedule = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const { user } = useAuth();
  // ACL — preserved from commit 0c77673. Read access is enough for the
  // schedule view; check-in / move-to-exam require write capability.
  const { canSchedulerWrite, canSchedulerRead } = capabilitiesFor(user);
  const [view, setView] = useState('list');

  /* ─── Real-mode data wiring (P0 fix follow-up) ───────────────── */
  const [appointments, setAppointments] = useState(() => (isDemo ? DEMO_APPOINTMENTS : []));
  const [loading, setLoading] = useState(!isDemo && canSchedulerRead);
  const [error, setError]     = useState(null);

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const fetchAppts = useCallback(async () => {
    if (isDemo) {
      setAppointments(DEMO_APPOINTMENTS);
      setLoading(false);
      return;
    }
    if (!canSchedulerRead) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await schedulerApi.listAppointmentsPreferEnriched({ date: todayIso });
    if (res.ok) {
      const raw = res.data?.appointments || res.data?.items || [];
      setAppointments(raw.map(normalizeAppt));
    } else {
      setError(res.error || { message: 'Failed to load today\'s schedule' });
      setAppointments([]);
    }
    setLoading(false);
  }, [isDemo, canSchedulerRead, todayIso]);

  useEffect(() => { fetchAppts(); }, [fetchAppts]);

  const checkIn = async (id) => {
    if (!canSchedulerWrite) { toast.info('Read-only role'); return; }
    if (isDemo) {
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, status: 'checked_in' } : a));
      toast.success('Patient checked in');
      return;
    }
    const res = await schedulerApi.checkinAppointment(id);
    if (res.ok) {
      toast.success('Patient checked in');
      fetchAppts();
    } else {
      toast.error(res.error?.message || 'Check-in failed');
    }
  };

  const moveToExam = async (id) => {
    if (!canSchedulerWrite) { toast.info('Read-only role'); return; }
    if (isDemo) {
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, status: 'in_exam', room: 'Room 3' } : a));
      toast.success('Moved to exam room');
      return;
    }
    const res = await schedulerApi.updateAppointmentStatus(id, { status: 'in_exam' });
    if (res.ok) {
      toast.success('Moved to exam room');
      fetchAppts();
    } else {
      toast.error(res.error?.message || 'Update failed');
    }
  };

  const today = new Date();

  return (
    <div className="space-y-6" data-testid="echo-schedule-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Schedule</h1>
          <p className="text-slate-500 mt-1">{today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
        <div className="flex gap-2">
          {!isDemo && (
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAppts}
              disabled={loading}
              className="gap-1.5"
              data-testid="schedule-refresh-btn"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} /> Refresh
            </Button>
          )}
          <div className="flex rounded-lg border overflow-hidden">
            <Button variant={view === 'list' ? 'default' : 'ghost'} size="sm" className={cn("rounded-none text-xs", view === 'list' && "bg-black text-white")} onClick={() => setView('list')} data-testid="view-list-btn">List</Button>
            <Button variant={view === 'timeline' ? 'default' : 'ghost'} size="sm" className={cn("rounded-none text-xs", view === 'timeline' && "bg-black text-white")} onClick={() => setView('timeline')} data-testid="view-timeline-btn">Timeline</Button>
            <Button variant={view === 'flow' ? 'default' : 'ghost'} size="sm" className={cn("rounded-none text-xs gap-1", view === 'flow' && "bg-black text-white")} onClick={() => setView('flow')} data-testid="view-flow-btn"><Kanban className="h-3 w-3" />Patient Flow</Button>
          </div>
          {canSchedulerWrite && (
            <Button onClick={() => navigate('/echo/lens')} className="bg-black hover:bg-gray-800 gap-2" data-testid="new-appt-btn"><Plus className="h-4 w-4" /> New Appointment</Button>
          )}
        </div>
      </div>

      {view === 'flow' ? (
        <EchoPatientFlow />
      ) : (
        <>
          {/* Status Overview */}
          <div className="flex gap-3 flex-wrap">
            {Object.entries(STATUS_CONFIG).slice(0, 4).map(([key, cfg]) => {
              const count = appointments.filter(a => a.status === key).length;
              return (
                <Badge key={key} variant="outline" className="gap-1.5 px-3 py-1.5">
                  <div className={cn("h-2 w-2 rounded-full", cfg.dot)} />
                  <span className="text-xs">{cfg.label}: {count}</span>
                </Badge>
              );
            })}
          </div>

          {/* AI Suggestion */}
          {appointments.some(a => a.noShowRisk > 30) && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-3 flex items-center gap-3">
                <Sparkles className="h-4 w-4 text-amber-600 shrink-0" />
                <p className="text-xs text-amber-800 flex-1">
                  <span className="font-semibold">AI Alert:</span> {appointments.filter(a => a.noShowRisk > 30).length} patient(s) have high no-show risk. Consider sending reminders.
                </p>
                <Button variant="outline" size="sm" className="h-7 text-xs border-amber-300" onClick={() => toast.success('Reminders sent to high-risk patients')}>Send Reminders</Button>
              </CardContent>
            </Card>
          )}

          {/* Loading / error / empty banners ─ explicit no-fallback states. */}
          {loading && appointments.length === 0 && (
            <Card className="border-slate-200" data-testid="schedule-loading">
              <CardContent className="p-8 flex items-center justify-center gap-2 text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading today's schedule…</span>
              </CardContent>
            </Card>
          )}
          {error && !loading && (
            <Card className="border-red-200 bg-red-50" data-testid="schedule-error">
              <CardContent className="p-6 text-center">
                <AlertTriangle className="h-5 w-5 text-red-500 mx-auto mb-1.5" />
                <p className="text-sm font-medium text-red-700">Could not load today's schedule</p>
                <p className="text-[11px] text-red-600 mt-0.5">{error.message || 'Server error — please retry.'}</p>
                <Button size="sm" variant="outline" className="mt-3 border-red-300 text-red-700" onClick={fetchAppts} data-testid="schedule-retry-btn">Retry</Button>
              </CardContent>
            </Card>
          )}
          {!loading && !error && appointments.length === 0 && (
            <Card className="border-dashed border-slate-200" data-testid="schedule-empty">
              <CardContent className="p-10 text-center">
                <Clock className="h-5 w-5 text-slate-300 mx-auto mb-1.5" />
                <p className="text-sm font-medium text-slate-600">No appointments today</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  {canSchedulerWrite
                    ? 'Open the scheduler to book a new appointment.'
                    : 'Your provider has not booked any visits for today.'}
                </p>
              </CardContent>
            </Card>
          )}

          {view === 'list' && appointments.length > 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {appointments.map((apt) => {
                    const st = STATUS_CONFIG[apt.status];
                    return (
                      <div key={apt.id} className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50 transition-colors" data-testid={`schedule-apt-${apt.id}`}>
                        <div className="w-20"><p className="text-sm font-semibold text-slate-800">{apt.time}</p><p className="text-[10px] text-muted-foreground">{apt.duration} min</p></div>
                        <div className={cn("h-2 w-2 rounded-full shrink-0", st.dot)} />
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/echo/patients/${apt.patientId}`)}>
                          <p className="text-sm font-medium text-slate-800 hover:text-violet-600 transition-colors">{apt.patient}</p>
                          <p className="text-xs text-muted-foreground">{apt.type} &middot; {apt.provider}</p>
                        </div>
                        {apt.room && <Badge variant="outline" className="text-[10px] gap-1"><MapPin className="h-2.5 w-2.5" />{apt.room}</Badge>}
                        <Badge className={cn("text-[10px]", st.color)}>{st.label}</Badge>
                        {apt.noShowRisk > 25 && <Badge variant="outline" className="text-[10px] border-red-200 text-red-600"><AlertTriangle className="h-2.5 w-2.5 mr-1" />{apt.noShowRisk}%</Badge>}
                        <div className="flex gap-1">
                          {apt.status === 'scheduled' && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => checkIn(apt.id)}>Check In</Button>}
                          {apt.status === 'waiting' && <Button size="sm" className="h-7 text-xs bg-black hover:bg-gray-800" onClick={() => moveToExam(apt.id)}>To Exam</Button>}
                          {apt.status === 'checked_in' && <Button size="sm" className="h-7 text-xs bg-black hover:bg-gray-800" onClick={() => moveToExam(apt.id)}>To Exam</Button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : view === 'timeline' && appointments.length > 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="space-y-1">
                  {HOURS.map((hour, hi) => {
                    const hourApts = appointments.filter(a => a.time.startsWith(hour.split(':')[0]));
                    return (
                      <div key={hi} className="flex gap-3 min-h-[48px]">
                        <span className="text-xs text-muted-foreground w-16 pt-1 shrink-0">{hour}</span>
                        <div className="flex-1 border-t border-slate-100 pt-1">
                          <div className="flex gap-2 flex-wrap">
                            {hourApts.map(apt => {
                              const st = STATUS_CONFIG[apt.status];
                              return (
                                <div key={apt.id} className={cn("px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer", st.color)} onClick={() => navigate(`/echo/patients/${apt.patientId}`)}>
                                  {apt.patient} · {apt.type} · {apt.provider}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
};

export default EchoSchedule;
